import React, { FC, useCallback, useMemo } from 'react';

import { UseFormReturn, useWatch } from 'react-hook-form';

import { MIN_USD_PRICE } from 'constants/constants';
import { EPopupSubType } from 'constants/enums';
import { OfferScheduleSection } from 'views/Offers/components/Schedule';
import {
  GeneralSection,
  OfferProgressBarSection,
  SaleDiscountSection,
  SegmentsSection
} from 'views/Offers/components/sections';

import Checkbox from 'components/Checkbox/Checkbox';
import FormSectionsTabs from 'components/FormSectionsTabs';
import InputSelectDropdown from 'components/InputSelectDropdown/InputSelectDropdown';
import { FormControl, FormField, FormItem } from 'components/ui';
import { Separator } from 'components/ui/separator';
import { Tabs, TabsContent } from 'components/ui/tabs';

import {
  EPopupTriggersAction,
  getPriorityOptions,
  PopupsFormFields,
  PRIORITY_NONE_VALUE
} from '../PopupsForm.consts';

import { ProductsSection } from './Sections/ProductsSection';
import { TriggersSection } from './Sections/TriggersSection';

interface IProgressBarValues {
  progressBarId: string | null;
  progressBarPoints: number | null;
}

interface ITriggeredPopupsContentProps {
  form: UseFormReturn<PopupsFormFields>;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  offerDesignOptions: { externalId: string; value: string; label: string }[];
  isDailyBonus: boolean;
  isNameTaken: boolean;
  offersIds: { value: string; key: string; content: string }[];
  getProducts: any;
  productsList: any[];
  addProduct: (product: any) => void;
  deleteProduct: (id: string) => void;
  updateProduct: (id: string, product: any) => void;
  formatProductQuantity: any;
  startHour: string;
  setStartHour: (hour: string) => void;
  intervalCron: string;
  setIntervalCron: (cron: string) => void;
  progressBarInitialValues: IProgressBarValues;
  onProgressBarChange: (
    values: IProgressBarValues,
    isValid: boolean,
    isDirty: boolean
  ) => void;
}

const TriggeredPopupsContent: FC<ITriggeredPopupsContentProps> = ({
  form,
  activeTab,
  setActiveTab,
  offerDesignOptions,
  isDailyBonus,
  isNameTaken,
  offersIds,
  getProducts,
  productsList,
  addProduct,
  deleteProduct,
  updateProduct,
  formatProductQuantity,
  startHour,
  setStartHour,
  intervalCron,
  setIntervalCron,
  progressBarInitialValues,
  onProgressBarChange
}): React.ReactElement => {
  const triggersAction = form.watch('triggersAction');
  const isFree = useWatch({ control: form.control, name: 'setAsFree' });
  const watchedPrice = useWatch({ control: form.control, name: 'price' });

  const isPriceEditable = !isDailyBonus && !isFree;

  const priceConfig = useMemo(() => {
    let type: 'number' | 'text' = 'number';
    if (!isPriceEditable) {
      type = 'text';
    }
    return {
      disabled: !isPriceEditable,
      type,
      displayValue: 'Free',
      min: MIN_USD_PRICE
    };
  }, [isPriceEditable]);

  const priorityOptions = useMemo(() => getPriorityOptions(), []);

  const renderPriorityField = useCallback(
    ({
      field: { value, onChange }
    }: {
      field: { value: number | null; onChange: (...event: unknown[]) => void };
    }): React.ReactElement => {
      const dropdownValue = value === null ? PRIORITY_NONE_VALUE : value;
      const selectedPriority = priorityOptions.find(
        (option) => option.value === dropdownValue
      );
      const handlePriorityChange = (newValue: string | number): void => {
        onChange(newValue === PRIORITY_NONE_VALUE ? null : newValue);
      };
      return (
        <FormItem>
          <FormControl>
            <InputSelectDropdown
              key={String(dropdownValue)}
              label="Priority"
              placeholder="Enter Number"
              defaultSelected={selectedPriority}
              options={priorityOptions}
              onOptionChange={handlePriorityChange}
              listDataTestId="dashPopupPriorityList"
              buttonDataTestId="popupPriorityBox"
            />
          </FormControl>
        </FormItem>
      );
    },
    [priorityOptions]
  );

  const handleCheckboxChange = useCallback(
    (checked: boolean): void => {
      form.setValue('setAsFree', checked, {
        shouldDirty: true,
        shouldValidate: true
      });
      if (!isDailyBonus) {
        form.setValue('price', checked ? 0 : MIN_USD_PRICE, {
          shouldDirty: true,
          shouldValidate: true
        });
      }
    },
    [form, isDailyBonus]
  );

  const handleTriggersActionChange = useCallback(
    (action: EPopupTriggersAction): void => {
      const subType =
        action === EPopupTriggersAction.STORE_REFRESH
          ? EPopupSubType.STORE_REFRESH
          : EPopupSubType.POST_PURCHASE;
      form.setValue('subType', subType, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  const handleSegmentsChange = useCallback(
    (segments: string[]): void => {
      form.setValue('segments', segments, {
        shouldDirty: true,
        shouldValidate: true
      });
    },
    [form]
  );

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
      <FormSectionsTabs
        currentTab={activeTab}
        tabs={[
          { value: 'settings', label: 'Settings' },
          { value: 'configuration', label: 'Configuration' }
        ]}
      />
      <TabsContent
        value="settings"
        forceMount
        className="mt-9 flex flex-col gap-y-9 pb-10 data-[state=inactive]:hidden"
      >
        <GeneralSection
          offerDesignOptions={offerDesignOptions}
          isNameTaken={isNameTaken}
          priceConfig={priceConfig}
          offerDesignListDataTestId="dashPopupDesignList"
          offerDesignButtonDataTestId="popupDesignBox"
        >
          <div className="grid grid-cols-2 gap-5">
            <FormField
              control={form.control}
              name="priority"
              render={renderPriorityField}
            />
          </div>
          {!isDailyBonus && (
            <Checkbox
              checked={isFree}
              onCheckedChange={handleCheckboxChange}
              label="Set As Free"
            />
          )}
        </GeneralSection>
      </TabsContent>
      <TabsContent
        value="configuration"
        forceMount
        className="mt-9 flex flex-col gap-y-5 data-[state=inactive]:hidden"
      >
        <TriggersSection
          control={form.control}
          offersIds={offersIds}
          isTriggeredPopups
          triggersAction={triggersAction}
          onTriggersActionChange={handleTriggersActionChange}
        />
        {triggersAction === EPopupTriggersAction.STORE_REFRESH && (
          <OfferScheduleSection
            startHour={startHour}
            setStartHour={setStartHour}
            intervalCron={intervalCron}
            setIntervalCron={setIntervalCron}
          />
        )}
        <Separator className="bg-base-border" />
        <SegmentsSection
          items={form.watch('segments') || []}
          onItemsChange={handleSegmentsChange}
          listTestId="popupSegmentsList"
        />
        <React.Fragment>
          <Separator className="bg-base-border" />
          <SaleDiscountSection showPriceDiscount={(watchedPrice ?? 0) > 0} />
          <Separator className="bg-base-border" />
          <OfferProgressBarSection
            initialValues={progressBarInitialValues}
            onChange={onProgressBarChange}
          />
        </React.Fragment>
        <Separator className="bg-base-border" />
        <ProductsSection
          form={form}
          isDailyBonus={isDailyBonus}
          formatProductQuantity={formatProductQuantity}
          products={getProducts}
          productsList={productsList}
          addProduct={addProduct}
          deleteProduct={deleteProduct}
          updateProduct={updateProduct}
        />
      </TabsContent>
    </Tabs>
  );
};

export { TriggeredPopupsContent };
